/**
 * OAuth State Manager
 *
 * Securely manages OAuth state tokens with automatic expiration
 * to prevent token replay attacks.
 * Uses localStorage (cleared when tab closes) for security.
 */

import Logger from './logger';

interface OAuthState {
  state: string;
  provider: string;
  timestamp: number;
  expiresAt: number;
}

const STATE_KEY_PREFIX = 'oauth_state_';
const STATE_EXPIRATION_MS = 10 * 60 * 1000; // 10 minutes

class OAuthStateManager {
  /**
   * Store OAuth state with expiration
   */
  setState(state: string, provider: string): void {
    const now = Date.now();
    const stateData: OAuthState = {
      state,
      provider,
      timestamp: now,
      expiresAt: now + STATE_EXPIRATION_MS
    };

    try {
      localStorage.setItem(
        `${STATE_KEY_PREFIX}${state}`,
        JSON.stringify(stateData)
      );

      // Also store reference for easy cleanup
      this.addToStateIndex(state);

      // Clean up expired states
      this.cleanupExpiredStates();
    } catch (error) {
      Logger.error('Failed to store OAuth state:', error);
    }
  }

  /**
   * Get OAuth state if valid and not expired
   */
  getState(state: string): OAuthState | null {
    try {
      const key = `${STATE_KEY_PREFIX}${state}`;
      const stored = localStorage.getItem(key);

      if (!stored) {
        return null;
      }

      const stateData: OAuthState = JSON.parse(stored);
      const now = Date.now();

      // Check if expired
      if (now > stateData.expiresAt) {
        Logger.warn('OAuth state has expired');
        this.removeState(state);
        return null;
      }

      return stateData;
    } catch (error) {
      Logger.error('Failed to retrieve OAuth state:', error);
      return null;
    }
  }

  /**
   * Remove a specific OAuth state
   */
  removeState(state: string): void {
    try {
      localStorage.removeItem(`${STATE_KEY_PREFIX}${state}`);
      this.removeFromStateIndex(state);
    } catch (error) {
      Logger.error('Failed to remove OAuth state:', error);
    }
  }

  /**
   * Clean up all expired OAuth states
   */
  cleanupExpiredStates(): void {
    try {
      const stateIndex = this.getStateIndex();
      const now = Date.now();

      stateIndex.forEach(state => {
        const key = `${STATE_KEY_PREFIX}${state}`;
        const stored = localStorage.getItem(key);

        if (stored) {
          try {
            const stateData: OAuthState = JSON.parse(stored);
            if (now > stateData.expiresAt) {
              this.removeState(state);
            }
          } catch {
            // Invalid data, remove it
            this.removeState(state);
          }
        } else {
          // State missing, remove from index
          this.removeFromStateIndex(state);
        }
      });
    } catch (error) {
      Logger.error('Failed to cleanup expired states:', error);
    }
  }

  /**
   * Clear all OAuth states
   */
  clearAllStates(): void {
    try {
      const stateIndex = this.getStateIndex();
      stateIndex.forEach(state => {
        this.removeState(state);
      });
      localStorage.removeItem('oauth_state_index');
    } catch (error) {
      Logger.error('Failed to clear all OAuth states:', error);
    }
  }

  /**
   * Get the index of all stored states
   */
  private getStateIndex(): string[] {
    try {
      const index = localStorage.getItem('oauth_state_index');
      return index ? JSON.parse(index) : [];
    } catch {
      return [];
    }
  }

  /**
   * Add a state to the index
   */
  private addToStateIndex(state: string): void {
    try {
      const index = this.getStateIndex();
      if (!index.includes(state)) {
        index.push(state);
        localStorage.setItem('oauth_state_index', JSON.stringify(index));
      }
    } catch (error) {
      Logger.error('Failed to update state index:', error);
    }
  }

  /**
   * Remove a state from the index
   */
  private removeFromStateIndex(state: string): void {
    try {
      const index = this.getStateIndex();
      const filtered = index.filter(s => s !== state);
      localStorage.setItem('oauth_state_index', JSON.stringify(filtered));
    } catch (error) {
      Logger.error('Failed to update state index:', error);
    }
  }
}

// Export singleton instance
export const oauthStateManager = new OAuthStateManager();

// Clean up expired states on module load
oauthStateManager.cleanupExpiredStates();

// Set up periodic cleanup (every 5 minutes)
if (typeof window !== 'undefined') {
  setInterval(() => {
    oauthStateManager.cleanupExpiredStates();
  }, 5 * 60 * 1000);
}
